import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/site/PageShell";
import { ServicePage } from "@/components/site/ServicePage";
import heroImg from "@/assets/portfolio-5.jpg";
import heroImgWebp from "@/assets/portfolio-5.webp";
import { seoMeta, canonicalLink, robotsMeta } from "@/lib/seo";
import { contenido } from "@/content/pages/merchandising-corporativo";
import { vistaServicio } from "@/lib/page-content/vista";
import type { ServiceSchemaData } from "@/lib/seo-schema-apply";
import { datosHeadServicio, jsonLdServicio } from "@/lib/jsonld-servicios";

const DEFAULT_TITLE = "Merchandising y Regalos Corporativos para Empresas | Improve";
const DEFAULT_DESCRIPTION =
  "Merchandising para empresas con tu logo: artículos promocionales, textiles, regalos corporativos y material POP. Diseño incluido y envío a todo el Perú.";

// Imagen provisional (del sitio) hasta asignar una propia al slot «servicio-merchandising-corporativo» desde el panel.
const props = {
  slug: "/merchandising-corporativo" as const,
  heroImg,
  heroImgWebp,
  heroSrcset:
    "/assets/portfolio-5-400w.webp 400w, /assets/portfolio-5-680w.webp 680w, /assets/portfolio-5-800w.webp 800w",
};
const relatedSlugs = [
  "/eventos-corporativos",
  "/stands-para-ferias",
  "/merchandising-corporativo",
] as const;

function MerchandisingCorporativoPage() {
  const { siteImageSlots, pageContent } = Route.useRouteContext();
  const vista = vistaServicio(contenido, pageContent);
  const slot = siteImageSlots?.["servicio-merchandising-corporativo"];
  return (
    <PageShell>
      <ServicePage
        {...props}
        vista={vista}
        heroImg={slot?.src ?? props.heroImg}
        heroImgWebp={slot?.src ?? props.heroImgWebp}
        heroSrcset={slot?.srcSet ?? props.heroSrcset}
        relatedSlugs={[...relatedSlugs]}
      />
    </PageShell>
  );
}

export const Route = createFileRoute("/merchandising-corporativo")({
  component: MerchandisingCorporativoPage,
  // El head() solo lee lo que arma el loader: el loader va en el chunk de la ruta junto con el
  // contenido, y así el contenido no entra al chunk de entrada de todas las páginas.
  loader: ({ context }) => datosHeadServicio(vistaServicio(contenido, context.pageContent)),
  head: ({ match, loaderData }) => {
    const override = match.context.seoOverride;
    const slot = match.context.siteImageSlots?.["servicio-merchandising-corporativo"];
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/merchandising-corporativo",
          image: override?.ogImage ?? slot?.src ?? heroImg,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: [
        ...canonicalLink(override?.canonicalPath ?? "/merchandising-corporativo"),
        {
          rel: "preload",
          as: "image",
          href: slot?.src ?? heroImgWebp,
          type: "image/webp",
          fetchPriority: "high",
        },
      ],
      scripts: loaderData
        ? jsonLdServicio(
            loaderData,
            "/merchandising-corporativo",
            slot?.src ?? heroImg,
            override?.schemaType === "service"
              ? (override.schemaData as Partial<ServiceSchemaData>)
              : null,
          )
        : [],
    };
  },
});
